import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { useDebounce } from '@blaze-react/utils/lib/customHooks';
import useLocalStorage from '../useLocalStorage';
import SearchBar from './SearchBar';
import RecentSearches from './RecentSearches';
import SearchResults from './SearchResults';
import TriggerModal from './TriggerModal';
import SearchAdminSideBar from './SearchAdminSideBar';
import useSearchResults from '../useSearchResults';
import { SEARCH_ADMIN } from '../../constants';

const SearchAdmin = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openSearchSideBar, setOpenSearchSideBar] = useState(true);
  const [entityValues, setEntityValues] = useState([]);
  const [recentSearches, setRecentSearches] = useLocalStorage('recentSearches', []);
  const [displaySearchModalFrame, setDisplaySearchModalFrame] = useState(false);
  const [enableResults, setEnableResults] = useState(true);
  const searchInputRef = useRef(null);
  const debouncedEntityValues = useDebounce(entityValues, 500);
  const entities = debouncedEntityValues.map(e => e.id);
  const [{ loading, newResults: results = [], total }, setQueryParams] = useSearchResults({
    queryKey: SEARCH_ADMIN,
    entities
  });

  useEffect(
    () => {
      if (searchInputRef && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    },
    [displaySearchModalFrame]
  );

  const handleSearchTerm = ({ target: { value } }) => {
    setSearchTerm(value);
  };

  const toggleSearchModal = () => {
    setSearchTerm(null);
    setEnableResults(false);
    setDisplaySearchModalFrame(!displaySearchModalFrame);
  };

  const search = ({ value }) => {
    if (!value) return;
    !enableResults && setEnableResults(true);
    const MAX_RECENT_SEARCHES = 5;
    const recentSearchesCopy = recentSearches.slice();

    if (!recentSearchesCopy.includes(value)) {
      recentSearchesCopy.push(value);
      if (recentSearchesCopy.length > MAX_RECENT_SEARCHES) {
        recentSearchesCopy.shift();
      }
    }

    setRecentSearches(recentSearchesCopy);
    setQueryParams({ value });
  };

  const handleKeyDown = ({ key }) => {
    if (key === 'Enter') {
      if (!searchTerm) return clear();
      search({ value: searchTerm });
    }
  };

  const clear = () => {
    setSearchTerm('');
    setEnableResults(false);
  };

  const searchModalResultsClassName = classnames('modal modal--full-screen modal--search', {
    'modal--show': displaySearchModalFrame
  });

  const searchModalSideBarClassName = classnames('modal__search__sidebar', {
    'modal__search__sidebar--close': !openSearchSideBar
  });

  const isEmptySearchTermAndData = !searchTerm && !results.length;
  const isEmptySearchTermnAndDisabledResults = !searchTerm && !enableResults;
  const shouldDisplayRecentSearches =
    isEmptySearchTermAndData || isEmptySearchTermnAndDisabledResults;

  const handleSetEntityValues = newEntity => {
    const entityExists = entityValues.some(entity => entity.id === newEntity.id);
    if (entityExists) {
      setQueryParams({ value: searchTerm, offset: 0 });
      setEntityValues(entityValues.filter(entity => entity.id !== newEntity.id));
    } else {
      setQueryParams({ value: searchTerm, offset: 0 });
      setEntityValues([...entityValues, newEntity]);
    }
  };

  return (
    <div className="search search--header">
      <TriggerModal toggleSearchModal={toggleSearchModal} />
      <div className={searchModalResultsClassName}>
        {displaySearchModalFrame && (
          <div className="modal__search">
            <SearchAdminSideBar
              searchModalSideBarClassName={searchModalSideBarClassName}
              openSearchSideBar={openSearchSideBar}
              setOpenSearchSideBar={setOpenSearchSideBar}
              handleSetEntityValues={handleSetEntityValues}
              entityValues={entityValues}
              setEntityValues={setEntityValues}
            />
            <div className="modal__search__content">
              <div className="modal__header modal__header--search">
                <SearchBar
                  toggleSearchModal={toggleSearchModal}
                  handleKeyDown={handleKeyDown}
                  searchInputRef={searchInputRef}
                  handleSearchTerm={handleSearchTerm}
                  searchTerm={searchTerm}
                  search={() => search({ value: searchTerm })}
                />
              </div>
              {shouldDisplayRecentSearches && (
                <RecentSearches
                  recentSearches={recentSearches}
                  handleSearchTerm={handleSearchTerm}
                  search={search}
                />
              )}
              {enableResults && (
                <SearchResults
                  currentValue={searchTerm}
                  entities={entities}
                  loading={loading}
                  results={results}
                  total={total}
                  setQueryParams={setQueryParams}
                  closeModal={toggleSearchModal}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchAdmin;
